import React, { useState } from 'react';
import { FileText, Sparkles, ArrowRight } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Loading } from '../ui/Loading';
import { Alert } from '../ui/Alert';
import { webhookService } from '../../services/api';
import { StyleAnalysisCard } from '../ui/StyleAnalysisCard';
import type { Brief, ArticleTypeId } from '../../types';

interface BriefGeneratorProps {
  initialData?: any;
  onComplete: (brief: Brief) => void;
  sessionId?: string;
  articleType?: ArticleTypeId;
}

export const BriefGenerator: React.FC<BriefGeneratorProps> = ({
  initialData,
  onComplete,
  sessionId,
  articleType = 'wikipedia',
}) => {
  const [rawText, setRawText] = useState(
    initialData?.rawText || ''
  );
  const [targetKeyword, setTargetKeyword] = useState(
    initialData?.targetKeyword || initialData?.topic || ''
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');

  // Si viene del formulario tradicional con palabra clave, generar automáticamente
  // const [autoGenerated, setAutoGenerated] = useState(false); // Eliminado por desuso


  const handleGenerate = async () => {
    if (!rawText.trim() || !targetKeyword.trim()) {
      setError('Por favor, completa todos los campos');
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      const brief = await webhookService.generateBrief(
        rawText.trim(),
        targetKeyword.trim(),
        undefined, // locale default
        sessionId || initialData?.sessionId,
        articleType
      );
      onComplete({ ...brief, sessionId: sessionId || initialData?.sessionId });
    } catch (error) {
      setError('Hubo un error al generar el brief. Por favor, intenta nuevamente.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Función para continuar sin generar brief (cuando viene del chat)
  const handleContinueWithoutBrief = () => {
    // Crear un brief "dummy" mínimo para que el flujo continúe
    const dummyBrief: Brief = {
      title: initialData?.topic || 'Artículo',
      mainKeyword: initialData?.targetKeyword || targetKeyword || '',
      secondaryKeywords: [],
      metaTitle: '',
      metaDescription: '',
      outline: initialData?.subtopics?.map((topic: string) => ({
        heading: topic,
        subheadings: []
      })) || [],
      seoRecommendations: [],
      targetWordCount: initialData?.wordCount || 1500
    };
    onComplete({ ...dummyBrief, sessionId: sessionId || initialData?.sessionId });
  };

  // Auto-generar SOLO si viene del formulario tradicional (NO del chat)
  React.useEffect(() => {
    // Si venimos del chat con subtemas ya definidos, NO auto-generar
    if (initialData?.subtopics || initialData?.briefData) {
      // setAutoGenerated(true); // Removed to fix checking error
      return;
    }

    // Auto-generar solo si viene del formulario tradicional
    // MODIFICACIÓN: Desactivamos la autogeneración para que el usuario siempre vea el paso intermedio
    // y pueda verificar los datos antes de pasar a la investigación.
  }, []);

  if (isGenerating) {
    return (
      <Card className="text-center">
        <Loading size="lg" text="Generando estructura del brief..." />
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-center space-x-3 mb-6">
          <FileText className="w-6 h-6 text-primary-600" />
          <h2 className="text-xl font-semibold">Generar Brief SEO</h2>
        </div>

        {error && (
          <div className="mb-4">
            <Alert type="error">
              {error}
            </Alert>
          </div>
        )}

        <div className="space-y-4">
          <Input
            label="Palabra clave objetivo"
            placeholder="Ej: marketing digital"
            value={targetKeyword}
            onChange={(e) => setTargetKeyword(e.target.value)}
            helperText="La palabra clave principal para tu artículo"
          />

          <Textarea
            label="Información del briefing"
            placeholder="Información recopilada del briefing conversacional..."
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            rows={10}
            helperText="Toda la información que recopilamos en el briefing"
          />

          {/* Botón diferente según si viene del chat o del formulario */}
          {initialData?.subtopics && initialData.subtopics.length > 0 ? (
            // Viene del chat con subtemas: botón de continuar
            <Button
              onClick={handleContinueWithoutBrief}
              size="lg"
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
              icon={<ArrowRight className="w-5 h-5" />}
            >
              Continuar a Investigación con Perplexity
            </Button>
          ) : (
            // Viene del formulario: botón de generar brief
            <Button
              onClick={handleGenerate}
              size="lg"
              className="w-full"
              icon={<Sparkles className="w-5 h-5" />}
            >
              Generar Brief Estructurado
            </Button>
          )}
        </div>
      </Card>

      {/* Sub-panel con subtemas extraídos del chat */}
      {initialData?.subtopics && initialData.subtopics.length > 0 && (
        <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-blue-50">
          <div className="space-y-4">
            <div className="flex items-center space-x-3 pb-3 border-b border-purple-200">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-xl">📋</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Subtemas Extraídos del Chat
                </h3>
                <p className="text-sm text-gray-600">
                  Estos {initialData.subtopics.length} subtemas se investigarán con Perplexity
                </p>
              </div>
            </div>

            <div className="space-y-2">
              {initialData.subtopics.map((subtopic: string, index: number) => (
                <div
                  key={index}
                  className="flex items-start space-x-3 p-3 bg-white rounded-lg border border-purple-100 shadow-sm hover:shadow-md transition-shadow"
                >
                  <span className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    {index + 1}
                  </span>
                  <div className="flex-1 pt-1">
                    <p className="text-sm font-medium text-gray-900">
                      {subtopic}
                    </p>
                  </div>
                  <span className="text-green-500 text-lg">✓</span>
                </div>
              ))}
            </div>

            <div className="pt-3 border-t border-purple-200">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-2 text-purple-700">
                  <span>🔍</span>
                  <span className="font-medium">
                    Se investigará cada subtema individualmente
                  </span>
                </div>
                <div className="text-purple-600 font-semibold">
                  ⏱️ ~{initialData.subtopics.length * 15}s
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Style Analysis Section */}
      {initialData?.styleAnalysis && (
        <Card className="border-2 border-pink-200 bg-gradient-to-br from-pink-50 to-purple-50">
          <div className="space-y-4">
            <div className="flex items-center space-x-3 pb-3 border-b border-pink-200">
              <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-xl">🎨</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Estilo de Escritura Detectado
                </h3>
                <p className="text-sm text-gray-600">
                  Este estilo se aplicará en la generación final
                </p>
              </div>
            </div>

            <StyleAnalysisCard data={initialData.styleAnalysis} />
          </div>
        </Card>
      )}
    </div>
  );
};



